<template>
  <div class="comment_item" :class="{ unread: !isRead }" @click="onClick">
    <div class="left">
      <Avatar
        :rounded="true"
        :color="user.color"
        :src="user.avatarUrl || user.gravatarUrl"
        :alt="user.shortName"
        :initials="user.shortName"
      />
      <svg-icon
        v-if="comment.resolved"
        class="icon-check"
        :fill="'#db841e'"
        :width="'12'"
        :height="'12'"
        icon="app-icons/check"
      />
    </div>
    <div class="right">
      <div class="top">
        <div class="name">{{ user.fullName }}</div>
        <div class="timestamp">{{ elapsedTime }}</div>
      </div>
      <div class="content" v-html="comment.content" />
      <div class="replies">{{ repliesNumber }} replies</div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Avatar } from '@ui/components/avatar';
import { elapsedTimeToString } from '../../../utils.js';
import SvgIcon from '../SvgIcon.vue';

export default {
  components: {
    Avatar,
    SvgIcon,
  },

  props: {
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      elapsedTime: null,
      interval: 0,
    };
  },

  computed: {
    ...mapGetters('project/users', ['getParticipant']),
    ...mapGetters('user/account', ['getId']),
    ...mapGetters(['getServerTimeDiff']),
    ...mapGetters('project/data/comments', {
      getComment: 'getNode',
      getThread: 'getContainerNode',
    }),
    ...mapGetters('project/data/comments', ['getThreadLastReplyDate']),
    ...mapGetters('application', ['getCommentSection']),
    ...mapGetters('project/config/comments', ['getRead']),
    /**
     * The participant that we want to edit his/her role
     */
    user() {
      return (
        this.getParticipant(this.comment.userId) || {
          shortName: '?',
          fullName: 'Unknown User',
          avatarUrl: null,
          gravatarUrl: null,
          color: '#363636',
        }
      );
    },

    /**
     * The comment we are displaying
     */
    comment() {
      return this.getComment(this.id);
    },

    /**
     * The number of replies for this thread
     */
    repliesNumber() {
      return this.comment?.children?.length || 0;
    },

    /**
     * Return whether the current thread has been read
     * @returns {Boolean}
     */
    isRead() {
      return (
        this.getRead(this.comment.id) &&
        this.comment.children.every(id => this.getRead(id))
      );
    },
  },

  watch: {
    /**
     * Whenever the section is open update the elapsed time of threads at regular intervals
     */
    getCommentSection(newVal) {
      // When re-opening the comment's thread, recalculate elpased time
      if (newVal) {
        this.elapsedTime = this.calculateElapsedTime();
        this.interval = setInterval(() => {
          this.elapsedTime = this.calculateElapsedTime();
        }, 10000);
      } else {
        clearInterval(this.interval);
      }
    },

    /**
     * Whenever a reply is added recalculate elapsed time
     */
    repliesNumber() {
      this.elapsedTime = this.calculateElapsedTime();
    },
  },

  mounted() {
    this.elapsedTime = this.calculateElapsedTime();
  },

  beforeUnmount() {
    clearInterval(this.interval);
  },

  methods: {
    /**
     * Returns the time that has passed since the creation of the comment (server time)
     * @returns Number
     */
    calculateElapsedTime() {
      // When was the last comment reply created (server time)
      const creationTime = new Date(this.getThreadLastReplyDate(this.id));
      // Get current server time
      const serverTime = new Date(Date.now() - this.getServerTimeDiff);
      const elapsedTime = Math.round(
        (serverTime.getTime() - creationTime.getTime()) / 1000
      );
      return elapsedTimeToString(elapsedTime);
    },

    onClick() {
      this.$emit('clickEvent');
    },
  },
};
</script>

<style scoped lang="scss">
.comment_item.unread .left:after {
  content: '';
  position: absolute;
  top: 19px;
  left: 0px;
  width: 4px;
  height: 8px;
  background: #fff;
  border-radius: 0 999px 999px 0;
}
.comment_item {
  display: flex;
  align-items: flex-start;
  padding: 10px 10px 20px 10px;
  position: relative;

  &:hover {
    background: #353c3e;
    cursor: pointer;
    z-index: 0;
  }
  .left {
    .icon-check {
      margin-top: 5px;
    }
  }
  .right {
    width: 100%;
    margin: 0 0 0 8px;
    .top {
      margin: 3px 0 5px 0;
      display: flex;
      .name {
        flex: 1;
        font-size: 12px;
        font-weight: 400;
        margin: 0 10px 0 0;
        color: #828585;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .timestamp {
        flex-shrink: 0;
        // flex: 1;
        font-size: 12px;
        color: #828585;
      }
    }
    .content {
      color: #bcbcbc;
      :deep(p) {
        margin: 5px 0;
      }
    }
    .replies {
      flex: 0 0 100%;
      color: #828585;
      font-size: 12px;
    }
  }
}
</style>
